import type { ToolSpec } from '../types';

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
}

export const LFM2_TOKENS = {
  bos: '<|startoftext|>',
  start: '<|im_start|>',
  end: '<|im_end|>',
  callStart: '<|tool_call_start|>',
  callEnd: '<|tool_call_end|>',
} as const;

export function lfm2Tools(specs: ToolSpec[]): Array<Record<string, unknown>> {
  return specs.map((t) => ({
    name: t.name,
    description: t.description,
    parameters: t.input_schema,
  }));
}

// Mirrors LiquidAI/LFM2.5's chat_template.jinja, written out so the prompt is identical in every runtime.
export function lfm2Prompt(messages: ChatMessage[], tools: ToolSpec[]): string {
  const t = LFM2_TOKENS;
  let system = messages[0]?.role === 'system' ? messages[0].content : '';
  const rest = messages[0]?.role === 'system' ? messages.slice(1) : messages;
  if (tools.length)
    system +=
      (system ? '\n' : '') +
      'List of tools: [' +
      lfm2Tools(tools)
        .map((x) => JSON.stringify(x))
        .join(', ') +
      ']';
  let out = t.bos;
  if (system) out += `${t.start}system\n${system}${t.end}\n`;
  for (const m of rest) out += `${t.start}${m.role}\n${m.content}${t.end}\n`;
  out += `${t.start}assistant\n`;
  return out;
}

export interface ParsedCall {
  name: string;
  args: Record<string, unknown>;
}

// Tool calls come back as Python-style calls between the special tokens:
//   <|tool_call_start|>[quantity_takeoff(quantity="volume", material="concrete")]<|tool_call_end|>
// Small models drop the tokens now and then, so a bare [name(...)] filling the reply is accepted too.
export function parseToolCalls(text: string): ParsedCall[] {
  const t = LFM2_TOKENS;
  let body: string | null = null;
  const start = text.indexOf(t.callStart);
  if (start >= 0) {
    const end = text.indexOf(t.callEnd, start);
    body = text.slice(start + t.callStart.length, end >= 0 ? end : undefined);
  } else {
    const bare = text.trim();
    if (/^\[?\s*[A-Za-z_][\w]*\s*\(/.test(bare) && /\)\s*\]?$/.test(bare)) body = bare;
  }
  if (body == null) return [];
  body = body.trim();
  if (body.startsWith('[') && body.endsWith(']')) body = body.slice(1, -1);
  const calls: ParsedCall[] = [];
  const p = new Parser(body);
  while (true) {
    p.ws();
    if (p.done()) break;
    const name = p.ident();
    if (!name) break;
    p.ws();
    if (!p.eat('(')) break;
    const args: Record<string, unknown> = {};
    while (true) {
      p.ws();
      if (p.eat(')')) break;
      const key = p.ident();
      p.ws();
      if (!key || !p.eat('=')) return calls;
      p.ws();
      args[key] = p.value();
      p.ws();
      p.eat(',');
    }
    calls.push({ name, args });
    p.ws();
    p.eat(',');
  }
  return calls;
}

export function stripToolTokens(text: string): string {
  const t = LFM2_TOKENS;
  return text
    .replace(new RegExp(`${escape(t.callStart)}[\\s\\S]*?(${escape(t.callEnd)}|$)`, 'g'), '')
    .replace(new RegExp(escape(t.end), 'g'), '')
    .replace(new RegExp(escape(t.start) + '\\w+\\n?', 'g'), '')
    .trim();
}

const escape = (s: string) => s.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');

class Parser {
  private i = 0;
  constructor(private readonly s: string) {}
  done() {
    return this.i >= this.s.length;
  }
  ws() {
    while (!this.done() && /\s/.test(this.s[this.i]!)) this.i++;
  }
  eat(c: string) {
    if (this.s[this.i] === c) {
      this.i++;
      return true;
    }
    return false;
  }
  ident(): string | null {
    const m = /^[A-Za-z_][\w.]*/.exec(this.s.slice(this.i));
    if (!m) return null;
    this.i += m[0].length;
    return m[0];
  }
  value(): unknown {
    this.ws();
    const c = this.s[this.i];
    if (c === '"' || c === "'") return this.string(c);
    if (c === '[') {
      this.i++;
      const out: unknown[] = [];
      while (true) {
        this.ws();
        if (this.eat(']') || this.done()) return out;
        out.push(this.value());
        this.ws();
        this.eat(',');
      }
    }
    if (c === '{') {
      this.i++;
      const out: Record<string, unknown> = {};
      while (true) {
        this.ws();
        if (this.eat('}') || this.done()) return out;
        const k = this.value();
        this.ws();
        this.eat(':');
        out[String(k)] = this.value();
        this.ws();
        this.eat(',');
      }
    }
    const m = /^(-?\d+(?:\.\d+)?(?:e-?\d+)?|True|False|None|true|false|null)/.exec(
      this.s.slice(this.i),
    );
    if (m) {
      this.i += m[0].length;
      const w = m[0];
      if (w === 'True' || w === 'true') return true;
      if (w === 'False' || w === 'false') return false;
      if (w === 'None' || w === 'null') return null;
      return Number(w);
    }
    // bare word (unquoted string), up to the next delimiter
    const bare = /^[^,)\]}\s]+/.exec(this.s.slice(this.i));
    if (bare) {
      this.i += bare[0].length;
      return bare[0];
    }
    this.i++;
    return null;
  }
  private string(q: string): string {
    this.i++;
    let out = '';
    while (!this.done() && this.s[this.i] !== q) {
      if (this.s[this.i] === '\\' && this.i + 1 < this.s.length) {
        this.i++;
        const e = this.s[this.i]!;
        out += e === 'n' ? '\n' : e === 't' ? '\t' : e;
      } else out += this.s[this.i];
      this.i++;
    }
    this.i++;
    return out;
  }
}
